"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import {
  evaluatePathSelector,
  type PathSelectorQuestion,
  type PathSelectorResponses,
  type PathSelectorSchema,
} from "@/lib/gamification/path-selector-engine";
import {
  captureGamificationResultForCrm,
  gamificationTrackingAttributes,
  trackGamificationEngagement,
  type GamificationTrackingContext,
} from "@/components/gamification/tracking";
import PlayableExperienceCanvas from "@/components/gamification/PlayableExperienceCanvas";
import { useWebMcpTool } from "@/lib/webmcp/react";

export type PathSelectorToolCopy = {
  readonly eyebrow: string;
  readonly title: string;
  readonly intro: string;
  readonly recommendationLabel: string;
  readonly rankedOptionsLabel: string;
  readonly resetLabel: string;
};

type PathSelectorToolProps = {
  readonly copy: PathSelectorToolCopy;
  readonly schema: PathSelectorSchema;
  readonly tracking: GamificationTrackingContext;
};

function isSelected(question: PathSelectorQuestion, responses: PathSelectorResponses, optionId: string) {
  const value = responses[question.id];

  return question.multi ? Array.isArray(value) && value.includes(optionId) : value === optionId;
}

export default function PathSelectorTool({ copy, schema, tracking }: PathSelectorToolProps) {
  const [responses, setResponses] = useState<PathSelectorResponses>({});
  const startedRef = useRef(false);
  const capturedRecommendationRef = useRef("");
  const recommendationTrackedRef = useRef("");
  const suppressAutoCaptureRef = useRef(false);
  const result = useMemo(() => evaluatePathSelector(schema, responses), [schema, responses]);
  const firstOpenQuestionId = schema.questions.find((item) => !item.options.some((option) => isSelected(item, responses, option.id)))?.id;
  const canvasProgress = result.complete ? 100 : result.total > 0 ? Math.round((result.answered / result.total) * 100) : 0;
  const canvasNodes = schema.questions.map((item) => {
    const selectedOptions = item.options.filter((option) => isSelected(item, responses, option.id));

    return {
      id: item.id,
      label: item.label,
      detail: selectedOptions.map((option) => option.label).join(" / ") || item.description,
      active: item.id === firstOpenQuestionId,
      complete: selectedOptions.length > 0,
    };
  });
  const selectedTracks = schema.questions.flatMap((item) =>
    item.options
      .filter((option) => isSelected(item, responses, option.id))
      .map((option) => ({
        id: `${item.id}-${option.id}`,
        label: option.label,
        detail: option.description,
        complete: true,
      })),
  );
  const canvasTracks = result.complete
    ? result.rankedRecommendations.slice(0, 4).map((recommendation) => ({
        id: recommendation.id,
        label: recommendation.label,
        value: recommendation.recommendedServiceLine ?? recommendation.recommendedPath,
        complete: recommendation.id === result.recommendation.id,
      }))
    : selectedTracks;
  const toolSchema = useMemo(
    () =>
      z
        .object(
          Object.fromEntries(
            schema.questions.map((item) => {
              const optionIds = item.options.map((option) => option.id) as [string, ...string[]];
              return [item.id, item.multi ? z.array(z.enum(optionIds)) : z.enum(optionIds)];
            }),
          ) as z.ZodRawShape,
        )
        .strict(),
    [schema.questions],
  );
  useWebMcpTool({
    action: "recommend_service_path",
    instance: schema.id.replaceAll("-", "_"),
    schema: toolSchema,
    execute: (input) => {
      const nextResponses = input as PathSelectorResponses;
      const nextResult = evaluatePathSelector(schema, nextResponses);
      suppressAutoCaptureRef.current = true;
      setResponses(nextResponses);
      return { status: "success", uiState: nextResult };
    },
  });

  useEffect(() => {
    if (suppressAutoCaptureRef.current) {
      suppressAutoCaptureRef.current = false;
      return;
    }

    if (!result.complete || capturedRecommendationRef.current === result.recommendation.id) {
      return;
    }

    capturedRecommendationRef.current = result.recommendation.id;
    captureGamificationResultForCrm(tracking, result.recommendation.id, {
      service_line: result.recommendation.recommendedServiceLine,
    });
  }, [result.complete, result.recommendation.id, result.recommendation.recommendedServiceLine, tracking]);

  function trackStart() {
    if (startedRef.current) {
      return;
    }

    startedRef.current = true;
    trackGamificationEngagement(tracking, `${tracking.componentId}.start`, { xp_zone: "progress" });
  }

  function updateResponse(question: PathSelectorQuestion, optionId: string) {
    trackStart();
    recommendationTrackedRef.current = "";
    setResponses((current) => {
      if (!question.multi) {
        return { ...current, [question.id]: optionId };
      }

      const currentValue = current[question.id];
      const selected = Array.isArray(currentValue) ? currentValue : [];
      const nextSelected = selected.includes(optionId) ? selected.filter((id) => id !== optionId) : [...selected, optionId];

      return {
        ...current,
        [question.id]: nextSelected,
      };
    });
  }

  function trackRecommendation() {
    if (recommendationTrackedRef.current === result.recommendation.id) {
      return;
    }

    recommendationTrackedRef.current = result.recommendation.id;
    trackGamificationEngagement(tracking, `${tracking.componentId}.recommendation`, {
      recommended_path: result.recommendation.recommendedPath,
      recommended_service_line: result.recommendation.recommendedServiceLine,
      result_tier: result.recommendation.id,
      variant_key: result.recommendation.id,
      xp_zone: "panel",
    });
  }

  return (
    <section
      id={schema.id}
      className="redesign-section redesign-section--muted gamification-tool"
      aria-labelledby={`${schema.id}-title`}
      data-gamification-tool={schema.id}
      {...gamificationTrackingAttributes(tracking)}
    >
      <div className="container">
        <div className="marketing-section-heading marketing-section-heading--left">
          <p className="marketing-eyebrow">{copy.eyebrow}</p>
          <h2 id={`${schema.id}-title`}>{copy.title}</h2>
          <p>{copy.intro}</p>
        </div>

        <div className="gamification-tool__grid">
          <div className="gamification-tool__panel redesign-list-card">
            {schema.questions.map((question) => (
              <fieldset className="gamification-tool__fieldset" key={question.id}>
                <legend>{question.label}</legend>
                {question.description ? <p>{question.description}</p> : null}
                <div className="gamification-tool__options">
                  {question.options.map((option) => (
                    <label className="gamification-tool__option" key={option.id}>
                      <input
                        type={question.multi ? "checkbox" : "radio"}
                        name={question.id}
                        checked={isSelected(question, responses, option.id)}
                        onChange={() => updateResponse(question, option.id)}
                      />
                      <span>
                        <strong>{option.label}</strong>
                        {option.description ? <small>{option.description}</small> : null}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
            <button
              type="button"
              className="gamification-tool__link-button"
              onClick={() => {
                setResponses({});
                startedRef.current = false;
                capturedRecommendationRef.current = "";
                recommendationTrackedRef.current = "";
                suppressAutoCaptureRef.current = false;
              }}
            >
              {copy.resetLabel}
            </button>
          </div>

          <PlayableExperienceCanvas
            ariaLabel={copy.title}
            id={schema.id}
            nodes={canvasNodes}
            primaryLabel={result.complete ? result.recommendation.label : copy.recommendationLabel}
            progress={canvasProgress}
            secondaryLabel={result.complete ? result.recommendation.summary : copy.intro}
            tracks={canvasTracks}
            variant="path_selector"
          />

          {result.complete ? (
            <aside className="gamification-tool__result redesign-list-card" aria-live="polite" onMouseEnter={trackRecommendation} onFocus={trackRecommendation}>
              <p className="marketing-eyebrow">{copy.recommendationLabel}</p>
              <h3>{result.recommendation.label}</h3>
              <p>{result.recommendation.summary}</p>
              {result.recommendation.href && result.recommendation.ctaLabel ? (
                <a className="marketing-button marketing-button--primary" href={result.recommendation.href}>
                  {result.recommendation.ctaLabel}
                </a>
              ) : null}
              <div className="gamification-tool__result-block">
                <h4>{copy.rankedOptionsLabel}</h4>
                <ol>
                  {result.rankedRecommendations.slice(0, 3).map((recommendation) => (
                    <li key={recommendation.id}>{recommendation.label}</li>
                  ))}
                </ol>
              </div>
            </aside>
          ) : null}
        </div>
      </div>
    </section>
  );
}
